import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useResendScoreSheet } from "../hooks/useResendScoreSheet";
import { splitEmailAddresses } from "../lib/splitEmailAddresses";
import ResendClassNotPlaced from "./ResendClassNotPlaced";

// #846 -- report-api's admin-sheets.js resend route's error codes, mapped to
// curated, static i18n keys (never render a raw backend code/message; see
// common-fe's installServerErrorInterceptor -- it stashes the real detail on
// err.response.data but rewrites err.message to a generic "Server Error").
const RESEND_ERROR_KEYS = {
  validation_error: "rideEditor.resendInvalidEmail",
  no_email: "rideEditor.resendNoEmail",
  // #2844 -- the placed gate: a resend is an email, so it is refused (409)
  // until the ride's class is placed.
  class_not_placed: "rideEditor.resendClassNotPlaced",
  // #3357 -- a staff send goes to exactly one address.
  multiple_emails: "rideEditor.resendMultipleEmails",
};

// Score Admin's per-ride "Resend scoresheet" dialog (#846), extracted from
// RideEditor.jsx by #3357 so the address handling is testable on its own.
//
// #3357 -- the send goes ONLY to the one address staff entered or confirmed
// here. The rider's on-file field can hold several addresses (FVD free text);
// those are listed for reference but NEVER prefilled as a group or mailed
// wholesale, so there is no fan-out. A single on-file address is prefilled and
// still needs the explicit Send click. Mount only while open (state is seeded
// from `riderEmail` on mount, so a previous edit never lingers into the next).
//
// #3567 / ADR 0004 -- when the class is not placed and the caller passes the
// ride's `classId` (+ `classNumber`), the error says why the class is not
// placed and offers the fix as buttons (ResendClassNotPlaced), instead of only
// "try again once placed".
export default function ResendScoreSheetDialog({ rideId, riderEmail, staffRecipients = [], classId, classNumber, onClose, onSent }) {
  const { t } = useTranslation("scoreadmin");
  const { resend, sending } = useResendScoreSheet();
  const onFile = splitEmailAddresses(riderEmail);
  // Card #3610 -- staff already set this rider's Scoresheet recipients for the
  // show: leaving the box empty sends to all of them (the server reads the
  // list); a typed address is still a one-off to just that one.
  const hasStaffList = staffRecipients.length > 0;
  const [email, setEmail] = useState(!hasStaffList && onFile.length === 1 ? onFile[0] : "");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [classPlacedNow, setClassPlacedNow] = useState(false);

  const errorMessage = (err) => {
    if (err?.deliveryFailed) {
      return t("rideEditor.resendDeliveryFailed", { error: err.emailError || "" });
    }
    // #2844 -- the placed gate also refuses when it could not CONFIRM the class
    // is placed (fails closed); that is a "try again", not "not placed".
    if (
      err?.response?.data?.code === "class_not_placed" &&
      err?.response?.data?.data?.reason === "gate_lookup_failed"
    ) {
      return t("rideEditor.resendGateLookupFailed");
    }
    const code = err?.response?.data?.code || err?.clientCode;
    return t(RESEND_ERROR_KEYS[code] || "rideEditor.resendError");
  };

  const confirm = async () => {
    const typed = email.trim();
    if (!typed && !hasStaffList) return;
    if (splitEmailAddresses(typed).length > 1) {
      setError({ clientCode: "multiple_emails" });
      return;
    }
    try {
      setError(null);
      setClassPlacedNow(false);
      const outcome = await resend(rideId, typed || undefined);
      // #2844 -- the 202 only means the resend was dispatched; the send itself
      // is awaited server-side and its real outcome comes back as `delivery`.
      // A failed send must not be announced as "Sent."
      if (outcome?.delivery === "FAILED") {
        setError({ deliveryFailed: true, emailError: outcome.emailError });
      } else {
        setSuccess(true);
      }
      onSent?.(outcome);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Dialog open onClose={() => !sending && onClose()}>
      <DialogTitle>{t("rideEditor.resendDialogTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{hasStaffList ? t("rideEditor.resendDialogBodyStaffList") : t("rideEditor.resendDialogBody")}</DialogContentText>
        {hasStaffList && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="resend-staff-hint">
            {t("rideEditor.resendToRecipients", { addresses: staffRecipients.join(", ") })}
          </Alert>
        )}
        {!hasStaffList && onFile.length > 1 && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="resend-multi-hint">
            {t("rideEditor.resendMultipleOnFile", { addresses: onFile.join(", ") })}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          type="email"
          label={t("rideEditor.resendEmailLabel")}
          data-testid="resend-email-input"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSuccess(false);
          }}
        />
        {error && classId && error?.response?.data?.code === "class_not_placed" && (
          <ResendClassNotPlaced
            classId={classId}
            classNumber={classNumber}
            lookupFailed={error?.response?.data?.data?.reason === "gate_lookup_failed"}
            onRetry={confirm}
            onNavigate={onClose}
            onPlaced={() => {
              setError(null);
              setClassPlacedNow(true);
            }}
          />
        )}
        {error && !(classId && error?.response?.data?.code === "class_not_placed") && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(error)}
          </Alert>
        )}
        {classPlacedNow && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="resend-class-placed">
            {t("placeByRules.classPlacedSendNow", { classNumber })}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="resend-success">
            {t("rideEditor.resendSuccess")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={sending}>
          {t("rideEditor.resendCancel")}
        </Button>
        <Button
          variant="contained"
          loading={sending}
          disabled={(!email.trim() && !hasStaffList) || sending}
          data-testid="resend-confirm-button"
          onClick={confirm}
        >
          {t("rideEditor.resendConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
